export type ThemeId = "star-voyage" | "code-rain" | "kinetic-cloud" | "wheel";
export type AppearanceMode = "system" | "dark" | "light";
export type SoundPackId = "cinematic" | "party" | "rock" | "arcade";
export type MusicGenreId = "follow-pack" | SoundPackId | "off";
export type CelebrationVoiceId =
  | "auto"
  | "female"
  | "male"
  | "crowd"
  | "woohoo"
  | "congratulations"
  | "off";
export type DrawStatus = "ready" | "running" | "stopping" | "revealed";
export type DrawStopReason = "manual" | "auto-30s" | "auto-duration";
export type DrawRepeatMode = "repeat" | "no-repeat";
export type SourceKind = "paste" | "txt" | "csv" | "tsv" | "xlsx" | "json";

export interface PickerEntry {
  id: string;
  name: string;
  category: string | null;
  weight: number;
  enabled: boolean;
  source: {
    kind: SourceKind;
    fileName?: string;
    sheet?: string;
    row: number;
  };
}

export interface DrawRecord {
  id: string;
  drawNumber: number;
  timestamp: string;
  winnerId: string;
  winnerName: string;
  winnerCategory: string | null;
  winnerWeight: number;
  eligibleCount: number;
  categoryFilter: string[];
  repeatMode: DrawRepeatMode;
  keptInPool: boolean;
  stopReason: DrawStopReason;
  durationMs: number;
}

export interface PickscapeSettings {
  theme: ThemeId;
  appearance: AppearanceMode;
  durationSeconds: number;
  soundEnabled: boolean;
  soundPack: SoundPackId;
  celebrationVoice: CelebrationVoiceId;
  musicGenre: MusicGenreId;
  musicVolume: number;
  motionReduced: boolean;
  repeatMode: DrawRepeatMode;
  selectedCategories: string[];
}

export interface PickscapeProject {
  schemaVersion: 1;
  listName: string;
  entries: PickerEntry[];
  history: DrawRecord[];
  settings: PickscapeSettings;
  updatedAt: string;
}

export interface DrawRuntime {
  status: DrawStatus;
  winner: PickerEntry | null;
  eligibleSnapshot: PickerEntry[];
  startedAt: number | null;
  stopRequestedAt: number | null;
  stopReason: DrawStopReason | null;
  elapsedMs: number;
}

export interface ImportMapping {
  hasHeader: boolean;
  nameColumn: number;
  categoryColumn: number | null;
  weightColumn: number | null;
}

export interface ParsedImport {
  sourceKind: Exclude<SourceKind, "json">;
  fileName?: string;
  sheet?: string;
  rows: string[][];
  parseWarnings: string[];
}

export interface ValidatedImport {
  entries: PickerEntry[];
  duplicates: PickerEntry[];
  errors: Array<{ row: number; message: string }>;
  warnings: string[];
}

export const DEFAULT_SETTINGS: PickscapeSettings = {
  theme: "star-voyage",
  appearance: "dark",
  durationSeconds: 30,
  soundEnabled: true,
  soundPack: "cinematic",
  celebrationVoice: "auto",
  musicGenre: "follow-pack",
  musicVolume: 58,
  motionReduced: false,
  repeatMode: "no-repeat",
  selectedCategories: [],
};

export const EMPTY_PROJECT: PickscapeProject = {
  schemaVersion: 1,
  listName: "Untitled list",
  entries: [],
  history: [],
  settings: DEFAULT_SETTINGS,
  updatedAt: new Date(0).toISOString(),
};
